import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import SendIcon from "@mui/icons-material/Send";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

import {
  getProviders, saveContact, saveInvoiceEmail, getReport, sendReport,
} from "@/services/dispenseReportService";
import useAlert from "@/components/feedback/useAlert";

const NAVY = "#002169";
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const inputCls =
  "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-400";

const DispenseReportingPage = () => {
  const { showAlert, AlertSnackbar } = useAlert();

  const [providers, setProviders] = useState([]);
  const [invoiceEmail, setInvoiceEmail] = useState("");
  const [providerKey, setProviderKey] = useState("");
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(true);

  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProviders();
      setProviders(res.providers || []);
      setInvoiceEmail(res.invoiceEmail || "");
      setPeriod((prev) => prev || res.defaultPeriod);
      setProviderKey((prev) => prev || res.providers?.[0]?.key || "");
    } catch (e) {
      showAlert({ severity: "error", title: "Error", message: e.message || "Failed to load dispense providers." });
    }
    setLoading(false);
  }, [showAlert]);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  const run = async () => {
    if (!providerKey || !period) return;
    setRunning(true);
    setReport(null);
    try {
      const res = await getReport({ providerKey, month: period.month, year: period.year });
      setReport(res);
      setSubject(res.subject || "");
      setBody(res.body || "");
    } catch (e) {
      showAlert({ severity: "error", title: "Error", message: e.message || "Failed to run the report." });
    }
    setRunning(false);
  };

  const send = async () => {
    if (!report) return;
    setSending(true);
    try {
      const res = await sendReport({
        providerKey, month: period.month, year: period.year, subject, body,
      });
      showAlert({
        severity: "success",
        title: "Email sent",
        message: `${report.providerLabel} — ${report.monthLabel} (${report.count} pharmacies) sent to ${res.to}${res.cc ? `, cc ${res.cc}` : ""}.`,
        autoHideDuration: 8000,
      });
    } catch (e) {
      showAlert({ severity: "error", title: "Not sent", message: e.message || "Failed to send the email." });
    }
    setSending(false);
  };

  const patchContact = (key, field, value) =>
    setProviders((list) => list.map((p) => (p.key === key ? { ...p, [field]: value } : p)));

  const persistContact = async (p) => {
    try {
      await saveContact(p.key, { to_email: p.to_email || "", cc_email: p.cc_email || "" });
    } catch (e) {
      showAlert({ severity: "error", title: "Error", message: e.message || "Failed to save contact." });
    }
  };

  const currentYear = new Date().getFullYear();
  const selected = providers.find((p) => p.key === providerKey);

  if (loading) {
    return <div className="h-full flex items-center justify-center"><CircularProgress size={28} style={{ color: NAVY }} /></div>;
  }

  return (
    <div className="p-6 space-y-5">
      {AlertSnackbar}

      <div>
        <Link to="/admin" className="text-sm font-medium text-blue-600 hover:underline">← Admin</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">Dispense Reporting</h1>
        <p className="text-sm text-gray-600 mt-1 max-w-3xl">
          Monthly active-pharmacy counts to report to each dispense integration partner. A pharmacy
          counts if it holds an active Quda, MediDrop or HaloPay subscription, or created at least
          one DocLink consult during the month.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Provider</label>
            <select value={providerKey} onChange={(e) => { setProviderKey(e.target.value); setReport(null); }} className={inputCls}>
              {providers.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Month</label>
            <select value={period?.month ?? ""} onChange={(e) => { setPeriod((p) => ({ ...p, month: Number(e.target.value) })); setReport(null); }} className={inputCls}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Year</label>
            <select value={period?.year ?? ""} onChange={(e) => { setPeriod((p) => ({ ...p, year: Number(e.target.value) })); setReport(null); }} className={inputCls}>
              {Array.from({ length: 6 }, (_, i) => currentYear - i).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button type="button" onClick={run} disabled={running}
            className="px-5 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-50"
            style={{ backgroundColor: NAVY }}>
            {running ? "Running…" : "Run report"}
          </button>
          <div className="flex-1" />
          <button type="button" onClick={() => setShowConfig((v) => !v)}
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            <SettingsOutlinedIcon style={{ fontSize: 16 }} /> Email settings
          </button>
        </div>

        {selected && !selected.to_email && (
          <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            No email address configured for {selected.label} — set one under Email settings before sending.
          </p>
        )}
      </div>

      {showConfig && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="text-sm font-bold text-gray-800 mb-3">Email settings</h2>

          <div className="mb-4 max-w-md">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Send invoices to</label>
            <input
              type="email"
              value={invoiceEmail}
              onChange={(e) => setInvoiceEmail(e.target.value)}
              onBlur={() => saveInvoiceEmail(invoiceEmail).catch((e) => showAlert({ severity: "error", title: "Error", message: e.message }))}
              placeholder="admin@pharmacyhalo.com.au"
              className={inputCls}
            />
            <p className="text-xs text-gray-400 mt-1">Merged into every report email.</p>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">
                <th className="py-2">Provider</th>
                <th className="py-2">To</th>
                <th className="py-2">CC</th>
              </tr>
            </thead>
            <tbody>
              {providers.map((p) => (
                <tr key={p.key} className="border-b border-gray-50">
                  <td className="py-2 pr-3 font-medium text-gray-800 whitespace-nowrap">{p.label}</td>
                  <td className="py-2 pr-3">
                    <input type="email" value={p.to_email || ""} placeholder="partner@example.com"
                      onChange={(e) => patchContact(p.key, "to_email", e.target.value)}
                      onBlur={() => persistContact(p)} className={inputCls} />
                  </td>
                  <td className="py-2">
                    <input type="email" value={p.cc_email || ""} placeholder="optional"
                      onChange={(e) => patchContact(p.key, "cc_email", e.target.value)}
                      onBlur={() => persistContact(p)} className={inputCls} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {report && (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)] items-start">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase">{report.providerLabel} · {report.monthLabel}</p>
            <p className="text-5xl font-extrabold mt-2" style={{ color: NAVY }}>{report.count}</p>
            <p className="text-sm text-gray-500">active pharmacies</p>

            <div className="mt-4 max-h-72 overflow-y-auto border-t border-gray-100 pt-2">
              {report.chemists.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No active pharmacies this month.</p>
              ) : report.chemists.map((c) => (
                <div key={c.chemistId} className="py-1.5 border-b border-gray-50 last:border-b-0">
                  <p className="text-sm text-gray-800">{c.name}</p>
                  <p className="text-[11px] text-gray-400">
                    {[c.quda && "Quda", c.mediDrop && "MediDrop", c.haloPay && "HaloPay",
                      c.consults > 0 && `${c.consults} consult${c.consults === 1 ? "" : "s"}`]
                      .filter(Boolean).join(" · ")}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-bold text-gray-800 mb-3">Email preview</h2>
            <div className="text-xs text-gray-600 mb-3 space-y-0.5">
              <p><span className="font-semibold">To:</span> {report.to_email || <span className="text-amber-700">not configured</span>}</p>
              {report.cc_email && <p><span className="font-semibold">CC:</span> {report.cc_email}</p>}
            </div>

            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className={`${inputCls} mb-3`} />

            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Message</label>
            <textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)}
              className={`${inputCls} resize-none font-mono text-[13px]`} />

            <div className="flex justify-end mt-3">
              <button type="button" onClick={send} disabled={sending || !report.to_email}
                className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: NAVY }}>
                <SendIcon style={{ fontSize: 16 }} /> {sending ? "Sending…" : "Send email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DispenseReportingPage;
